// Much of this IFrame Logic is borrowed or based around https://git.hubteam.com/HubSpot/transcript-ui/blob/master/transcript-sidebar/static/js/components/TranscriptSidebar.js
import { useState, memo, useCallback, useRef, useEffect } from 'react';
import PortalIdParser from 'PortalIdParser';
import enviro from 'enviro';
import Raven from 'raven-js';
import I18n from 'I18n';
import { getFullUrl } from 'hubspot-url-utils';
import DeleteSubmissionModal from 'ui-deal-management-components-lib/components/deleteSubmissionModal/DeleteSubmissionModal';
//@ts-expect-error no types for ui-addon-iframeable
import UIModalIFrame from 'ui-addon-iframeable/host/UIModalIFrame';
//@ts-expect-error no types for ui-addon-iframeable
import { MSG_TYPE_MODAL_DIALOG_CLOSE } from 'ui-addon-iframeable/messaging/IFrameMessageTypes';
import { BASE_URL } from '../../constants/constants';
import { MSG_TYPE_CANCEL_DELETE_SUBMISSION, MSG_TYPE_CONFIRM_DELETE_OWN_SUBMISSION, MSG_TYPE_CONFIRM_DELETE_OTHER_SUBMISSION, MSG_TYPE_REFRESH_FORECAST_DATA_ACKED, MSG_TYPE_SHOW_DELETE_SUBMISSION_MODAL, MSG_TYPE_SUBMIT_SUCCESS, MSG_TYPE_SUBMIT_FAILURE, MSG_TYPE_SUBMIT_SUCCESS_ALL_PIPELINES, MSG_TYPE_TRACK_CLOSE_SUBMISSION_PANEL, MSG_TYPE_SHOW_DEALS_NOT_SAVED_ALERT } from '../../constants/messages';
import { showDeleteSubmissionSuccessAlert, showDeleteSubmissionFailureAlert } from '../../utils/alertUtils';
import { shouldTrack } from '../../utils/trackerUtils';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DATA_TEST_ID = 'forecastSubmissionIframe';
const handleError = errorParams => {
  Raven.captureMessage('[forecast-submission-panel-ui] Captured by the iframe', {
    extra: {
      errorParams
    }
  });
};
const getTargetUrl = () => `${getFullUrl(enviro.debug('is-local-submission-panel') ? 'local' : 'app')}${BASE_URL}/${PortalIdParser.get()}`;
const initialModalContext = {
  isModalOpen: false,
  forecastSubmission: {
    value: null,
    currencyCode: ''
  },
  forecastSubmissionId: 0,
  forecastSubmissionTimestamp: 0,
  isMostRecentSubmissionInPeriod: false,
  isOwnSubmission: false,
  isPeriodCompleteForAccuracy: false,
  isSubmissionOnlyRemaining: false,
  userSubmissionAmount: 0
};
const ForecastSubmissionPanel = ({
  containerId,
  dealIds,
  dealProperties,
  forecastAmount,
  forecastType,
  isDealStageView,
  ownerId,
  pipelineId,
  teamId,
  timePeriod,
  onClose,
  onSubmitSuccess,
  onSubmitFailure,
  onTrack
}) => {
  const modalRef = useRef(null);
  const [hostContext, setHostContext] = useState(null);
  const [shouldRefreshForecastData, setShouldRefreshForecastData] = useState(false);
  const [deleteSubmissionModalContext, setDeleteSubmissionModalContext] = useState(initialModalContext);
  const handleOnMessage = useCallback(message => {
    const {
      payload: {
        amount,
        cadence,
        fiscalOffset,
        prevAmount,
        submittedPipelineId,
        timePeriodValue,
        type
      }
    } = message;
    switch (type) {
      case MSG_TYPE_REFRESH_FORECAST_DATA_ACKED:
        setShouldRefreshForecastData(false);
        return;
      // No need to log this event
      case MSG_TYPE_SHOW_DELETE_SUBMISSION_MODAL:
        {
          const {
            forecastSubmission,
            forecastSubmissionId,
            forecastSubmissionTimestamp,
            isPeriodCompleteForAccuracy,
            isSubmissionMostRecent,
            isSubmissionOnlyRemaining,
            isOwnSubmission,
            userSubmissionAmount
          } = message.payload;
          setDeleteSubmissionModalContext({
            isModalOpen: true,
            forecastSubmission,
            forecastSubmissionId,
            forecastSubmissionTimestamp,
            isPeriodCompleteForAccuracy,
            isMostRecentSubmissionInPeriod: isSubmissionMostRecent,
            isSubmissionOnlyRemaining,
            isOwnSubmission,
            userSubmissionAmount
          });
          break;
        }
      case MSG_TYPE_SUBMIT_SUCCESS:
        // these values are passed such that a consumer can optimistically update the UI
        onSubmitSuccess({
          amount,
          prevAmount,
          submittedPipelineId,
          cadence,
          timePeriodValue,
          fiscalOffset,
          isAllPipelinesSubmission: false
        });
        break;
      case MSG_TYPE_SUBMIT_SUCCESS_ALL_PIPELINES:
        // these values are passed such that a consumer can optimistically update the UI
        onSubmitSuccess({
          amount,
          prevAmount,
          submittedPipelineId,
          cadence,
          timePeriodValue,
          fiscalOffset,
          isAllPipelinesSubmission: true
        });
        break;
      case MSG_TYPE_SUBMIT_FAILURE:
        {
          const {
            error
          } = message.payload;
          onSubmitFailure(error);
          return; // No need to log, handled by Raven
        }
      case MSG_TYPE_SHOW_DEALS_NOT_SAVED_ALERT:
        {
          const {
            submissionAction
          } = message.payload;
          FloatingAlertStore.addAlert({
            message: I18n.text(submissionAction === 'SUBMIT' ? 'forecastSubmissionPanel.notifications.dealsNotSaved.messageSubmitForecast' : 'forecastSubmissionPanel.notifications.dealsNotSaved.messageUpdateForecast'),
            titleText: I18n.text('forecastSubmissionPanel.notifications.dealsNotSaved.titleText'),
            type: 'warning'
          });
          break;
        }
      case MSG_TYPE_MODAL_DIALOG_CLOSE:
        onClose();
        return;
      default:
        break;
    }
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(type);
    }
  }, [onClose, onSubmitFailure, onSubmitSuccess, onTrack]);
  useEffect(() => {
    // TODO: Remove when https://git.hubteam.com/HubSpot/ui-addon-iframeable/issues/95 gets solved
    const fakeContext = {
      sendMessage: type => handleOnMessage({
        payload: {
          type
        }
      })
    };
    if (modalRef.current) {
      modalRef.current.hostContext = fakeContext;
      setHostContext(fakeContext);
    }
  }, [handleOnMessage]);
  const handleOnReady = _hostContext => {
    if (!hostContext) {
      setHostContext(_hostContext);
    }
  };
  const handleConfirmSubmissionDeletion = useCallback(() => {
    const {
      isMostRecentSubmissionInPeriod,
      isOwnSubmission
    } = deleteSubmissionModalContext;
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(isOwnSubmission ? MSG_TYPE_CONFIRM_DELETE_OWN_SUBMISSION : MSG_TYPE_CONFIRM_DELETE_OTHER_SUBMISSION);
    }
    setDeleteSubmissionModalContext(initialModalContext); // Dismisses modal before showing banner
    showDeleteSubmissionSuccessAlert();

    // If the deleted submission was the most recent, the host app needs to refresh
    if (isMostRecentSubmissionInPeriod) {
      window.parent.location.reload();
      return;
    }

    // If the deleted submission was not the most recent, re-fetch the panel data
    setShouldRefreshForecastData(true);
  }, [deleteSubmissionModalContext, onTrack]);
  const handleSubmissionDeletionError = useCallback(() => {
    showDeleteSubmissionFailureAlert();
    setDeleteSubmissionModalContext(initialModalContext);
  }, []);
  const handleCancelSubmissionDeletion = useCallback(() => {
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(MSG_TYPE_CANCEL_DELETE_SUBMISSION);
    }
    setDeleteSubmissionModalContext(initialModalContext);
  }, [onTrack]);
  const handleModalClose = useCallback(() => {
    if (shouldTrack(onTrack)) onTrack === null || onTrack === void 0 || onTrack(MSG_TYPE_TRACK_CLOSE_SUBMISSION_PANEL);
    onClose();
  }, [onClose, onTrack]);
  const {
    forecastSubmission,
    forecastSubmissionId,
    forecastSubmissionTimestamp,
    isModalOpen,
    isMostRecentSubmissionInPeriod,
    isPeriodCompleteForAccuracy,
    isSubmissionOnlyRemaining,
    userSubmissionAmount
  } = deleteSubmissionModalContext;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isModalOpen && /*#__PURE__*/_jsx(DeleteSubmissionModal, {
      forecastSubmission: forecastSubmission,
      forecastSubmissionId: forecastSubmissionId,
      forecastSubmissionTimestamp: forecastSubmissionTimestamp,
      handleCancel: handleCancelSubmissionDeletion,
      handleConfirmation: handleConfirmSubmissionDeletion,
      handleError: handleSubmissionDeletionError,
      isMostRecentSubmissionInPeriod: isMostRecentSubmissionInPeriod,
      isPeriodCompleteForAccuracy: isPeriodCompleteForAccuracy,
      isSubmissionForUser: !!ownerId,
      isSubmissionOnlyRemaining: isSubmissionOnlyRemaining,
      userSubmissionAmount: userSubmissionAmount
    }), /*#__PURE__*/_jsx(UIModalIFrame, {
      appName: "forecast-submission-panel-ui",
      containerZIndex: "1112",
      "data-unit-test": DATA_TEST_ID,
      embeddedPassthruProps: {
        containerId,
        dealProperties,
        externalDealIds: dealIds,
        externalForecastAmount: forecastAmount,
        externalPipelineId: pipelineId,
        externalRefreshSignal: shouldRefreshForecastData,
        externalTimePeriod: timePeriod,
        forecastType,
        isDealStageView,
        ownerId,
        teamId
      },
      iframePassthruProps: {
        id: DATA_TEST_ID,
        'data-test-id': DATA_TEST_ID
      },
      height: "100%",
      onClose: handleModalClose,
      onInitError: handleError,
      onMessage: handleOnMessage,
      onReady: handleOnReady,
      onReadyError: handleError,
      title: I18n.text('forecastSubmissionPanel.title'),
      use: "panel",
      ref: modalRef,
      scrolling: true,
      src: getTargetUrl(),
      width: 500
    })]
  });
};
export default /*#__PURE__*/memo(ForecastSubmissionPanel);